import React, { useEffect, useMemo, useState } from "react";
import { apiGetBroker, apiPatchBroker, apiPostBroker, apiDeleteBroker } from "../api";
import { useAuth } from "../components/AuthContext";

const PARTNER_FUNCTIONS = [
  { code: "SHIPPER", label: "Shipper", icon: "📦" },
  { code: "CONSIGNEE", label: "Consignee", icon: "📥" },
  { code: "CUSTOMER", label: "Customer", icon: "🏢" },
  { code: "PAYER", label: "Payer", icon: "💳" },
  { code: "BILL_TO", label: "Bill-To", icon: "🧾" },
  { code: "NOTIFY", label: "Notify Party", icon: "🔔" },
  { code: "LOGISTICS_CONTACT", label: "Logistics Contact", icon: "📞" },
  { code: "WAREHOUSE", label: "Warehouse", icon: "🏭" },
  { code: "CUSTOMS_BROKER", label: "Customs Broker", icon: "🛃" },
  { code: "IMPORTER", label: "Importer", icon: "📤" },
  { code: "EXPORTER", label: "Exporter", icon: "📤" },
];

function Badge({ label, color = "#2563eb", bg = "#eff6ff" }) {
  return (
    <span style={{
      display: "inline-flex", alignItems: "center", padding: "2px 8px",
      borderRadius: 12, fontSize: 11, fontWeight: 600,
      color, background: bg, border: `1px solid ${color}22`, marginRight: 4, marginBottom: 2
    }}>
      {label}
    </span>
  );
}

function FunctionBadge({ code }) {
  const fn = PARTNER_FUNCTIONS.find(f => f.code === code);
  if (!fn) return <Badge label={code} />;
  const colors = {
    SHIPPER: { color: "#0369a1", bg: "#e0f2fe" },
    CONSIGNEE: { color: "#7c3aed", bg: "#ede9fe" },
    CUSTOMER: { color: "#059669", bg: "#d1fae5" },
    PAYER: { color: "#d97706", bg: "#fef3c7" },
    BILL_TO: { color: "#dc2626", bg: "#fee2e2" },
    NOTIFY: { color: "#6366f1", bg: "#eef2ff" },
    LOGISTICS_CONTACT: { color: "#0891b2", bg: "#cffafe" },
    WAREHOUSE: { color: "#78716c", bg: "#f5f5f4" },
  };
  const c = colors[code] || { color: "#374151", bg: "#f3f4f6" };
  return <Badge label={`${fn.icon} ${fn.label}`} color={c.color} bg={c.bg} />;
}

export default function BrokerCustomers() {
  const { adminEmail } = useAuth();
  const [q, setQ] = useState("");
  const [customers, setCustomers] = useState([]);
  const [err, setErr] = useState("");
  const [ok, setOk] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [draft, setDraft] = useState({});
  const [contactDraft, setContactDraft] = useState({ firstName: "", lastName: "", email: "", phone: "", title: "", contactType: "GENERAL" });
  const [showAddContact, setShowAddContact] = useState(false);
  const [saving, setSaving] = useState(false);

  const shipperOptions = useMemo(() => {
    return (customers || []).map((c) => ({ id: c.id, name: c.name }));
  }, [customers]);

  async function refresh() {
    setErr(""); setOk("");
    try {
      const qs = q ? `?q=${encodeURIComponent(q)}` : "";
      const data = await apiGetBroker(`/broker/customers${qs}`, { adminEmail });
      setCustomers(data.customers || []);
    } catch (e) {
      setErr(e.message || String(e));
      setCustomers([]);
    }
  }

  useEffect(() => { refresh(); }, [adminEmail]);

  function startEdit(c) {
    setEditingId(c.id);
    setDraft({
      name: c.name || "",
      phone: c.phone || "",
      email: c.email || "",
      address1: c.address1 || "",
      city: c.city || "",
      state: c.state || "",
      postal: c.postal || "",
      country: c.country || "US",
      customerCode: c.customerCode || "",
      parentCompanyId: c.parentCompanyId || "",
      billingCompanyId: c.billingCompanyId || "",
      billingEmail: c.billingEmail || "",
      paymentTerms: c.paymentTerms || "",
      creditLimit: c.creditLimit || "",
      partnerFunctions: c.partnerFunctions || [],
    });
    setShowAddContact(false);
  }

  function toggleFunction(code) {
    setDraft(p => {
      const fns = p.partnerFunctions || [];
      return { ...p, partnerFunctions: fns.includes(code) ? fns.filter(f => f !== code) : [...fns, code] };
    });
  }

  async function save() {
    setErr(""); setOk(""); setSaving(true);
    try {
      await apiPatchBroker(`/broker/customers/${editingId}`, draft, { adminEmail });
      setOk("Saved successfully.");
      setEditingId(null);
      setDraft({});
      await refresh();
    } catch (e) {
      setErr(e.message || String(e));
    } finally { setSaving(false); }
  }

  async function addContact() {
    if (!contactDraft.firstName && !contactDraft.lastName) return;
    try {
      await apiPostBroker(`/broker/customers/${editingId}/contacts`, contactDraft, { adminEmail });
      setContactDraft({ firstName: "", lastName: "", email: "", phone: "", title: "", contactType: "GENERAL" });
      setShowAddContact(false);
      await refresh();
    } catch (e) {
      setErr(e.message || String(e));
    }
  }

  async function deleteContact(contactId) {
    try {
      await apiDeleteBroker(`/broker/customers/${editingId}/contacts/${contactId}`, { adminEmail });
      await refresh();
    } catch (e) {
      setErr(e.message || String(e));
    }
  }

  const sectionStyle = {
    marginTop: 18, padding: 20, border: "1px solid #e5e7eb", borderRadius: 12, background: "white"
  };
  const labelStyle = { display: "block", fontSize: 12, fontWeight: 600, color: "#374151", marginBottom: 4 };
  const inputStyle = { padding: 10, borderRadius: 6, border: "1px solid #d1d5db", width: "100%", fontSize: 14 };

  return (
    <div style={{ maxWidth: 1200, padding: 24 }}>
      <h2 style={{ margin: "0 0 20px" }}>🏢 Business Partners — Shippers & Customers</h2>

      {err && <div style={{ marginBottom: 14, padding: 12, background: "#fee2e2", border: "1px solid #fca5a5", borderRadius: 10, color: "#991b1b" }}>{err}</div>}
      {ok && <div style={{ marginBottom: 14, padding: 12, background: "#d1fae5", border: "1px solid #6ee7b7", borderRadius: 10, color: "#065f46" }}>{ok}</div>}

      {/* Search */}
      <div style={sectionStyle}>
        <div style={{ display: "flex", gap: 10 }}>
          <input value={q} onChange={e => setQ(e.target.value)} onKeyDown={e => e.key === "Enter" && refresh()}
            placeholder="Search by name..." style={{ ...inputStyle, flex: 1 }} />
          <button onClick={refresh} style={{ padding: "10px 20px", fontWeight: 700, background: "#2563eb", color: "white", border: "none", borderRadius: 6, cursor: "pointer" }}>
            Search
          </button>
        </div>
      </div>

      {/* List */}
      <div style={sectionStyle}>
        <div style={{ fontWeight: 800, marginBottom: 12, fontSize: 16 }}>Partners ({customers.length})</div>
        {customers.length === 0 ? (
          <div style={{ color: "#6b7280", padding: 20, textAlign: "center" }}>No partners found.</div>
        ) : (
          <div style={{ display: "grid", gap: 12 }}>
            {customers.map(c => {
              const isEditing = editingId === c.id;
              return (
                <div key={c.id} style={{
                  border: isEditing ? "2px solid #2563eb" : "1px solid #e5e7eb",
                  borderRadius: 12, padding: 16, background: isEditing ? "#fafbff" : "white"
                }}>
                  {/* Summary row */}
                  <div style={{ display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
                    <div style={{ fontWeight: 800, fontSize: 15 }}>{c.name}</div>
                    <div style={{ fontSize: 12, color: "#6b7280" }}>
                      {c.customerCode && <span>Code: <b>{c.customerCode}</b> • </span>}
                      {c.email && <span>{c.email} • </span>}
                      {c.city && c.state && <span>{c.city}, {c.state}</span>}
                    </div>
                    <div style={{ marginLeft: "auto" }}>
                      <button onClick={() => isEditing ? (setEditingId(null), setDraft({})) : startEdit(c)}
                        style={{ padding: "6px 14px", fontWeight: 700, borderRadius: 6, border: "1px solid #d1d5db", background: isEditing ? "#fee2e2" : "#f3f4f6", cursor: "pointer" }}>
                        {isEditing ? "✕ Close" : "Edit"}
                      </button>
                    </div>
                  </div>

                  {/* Partner functions badges */}
                  <div style={{ marginTop: 6, display: "flex", flexWrap: "wrap", gap: 2 }}>
                    {(c.partnerFunctions || []).map(fn => <FunctionBadge key={fn} code={fn} />)}
                    {(!c.partnerFunctions || c.partnerFunctions.length === 0) && (
                      <span style={{ fontSize: 11, color: "#9ca3af" }}>No partner functions assigned</span>
                    )}
                  </div>

                  {/* Relationships */}
                  {(c.parentCompany || c.billingCompany) && (
                    <div style={{ marginTop: 4, fontSize: 12, color: "#6b7280" }}>
                      {c.parentCompany && <span>Parent: <b>{c.parentCompany.name}</b> • </span>}
                      {c.billingCompany && <span>Billing: <b>{c.billingCompany.name}</b></span>}
                    </div>
                  )}

                  {/* Contacts summary */}
                  {c.contacts?.length > 0 && !isEditing && (
                    <div style={{ marginTop: 4, fontSize: 12, color: "#6b7280" }}>
                      Contacts: {c.contacts.map(co => `${co.first_name} ${co.last_name}`).join(", ")}
                    </div>
                  )}

                  {/* EDIT FORM */}
                  {isEditing && (
                    <div style={{ marginTop: 16, display: "grid", gap: 16 }}>
                      {/* Basic Info */}
                      <div style={{ background: "#f9fafb", borderRadius: 8, padding: 16 }}>
                        <div style={{ fontWeight: 700, marginBottom: 12 }}>📋 Company Information</div>
                        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12 }}>
                          <div><label style={labelStyle}>Company Name</label><input value={draft.name || ""} onChange={e => setDraft(p => ({ ...p, name: e.target.value }))} style={inputStyle} /></div>
                          <div><label style={labelStyle}>Customer Code</label><input value={draft.customerCode || ""} onChange={e => setDraft(p => ({ ...p, customerCode: e.target.value }))} style={inputStyle} /></div>
                          <div><label style={labelStyle}>Email</label><input value={draft.email || ""} onChange={e => setDraft(p => ({ ...p, email: e.target.value }))} style={inputStyle} /></div>
                          <div><label style={labelStyle}>Phone</label><input value={draft.phone || ""} onChange={e => setDraft(p => ({ ...p, phone: e.target.value }))} style={inputStyle} /></div>
                          <div><label style={labelStyle}>Payment Terms</label>
                            <select value={draft.paymentTerms || ""} onChange={e => setDraft(p => ({ ...p, paymentTerms: e.target.value }))} style={inputStyle}>
                              <option value="">Select...</option>
                              <option value="NET_15">Net 15</option>
                              <option value="NET_30">Net 30</option>
                              <option value="NET_45">Net 45</option>
                              <option value="NET_60">Net 60</option>
                              <option value="NET_90">Net 90</option>
                              <option value="DUE_ON_RECEIPT">Due on Receipt</option>
                              <option value="PREPAID">Prepaid</option>
                            </select>
                          </div>
                          <div><label style={labelStyle}>Credit Limit ($)</label><input type="number" value={draft.creditLimit || ""} onChange={e => setDraft(p => ({ ...p, creditLimit: e.target.value }))} style={inputStyle} /></div>
                        </div>

                        <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr 1fr 1fr", gap: 12, marginTop: 12 }}>
                          <div><label style={labelStyle}>Address</label><input value={draft.address1 || ""} onChange={e => setDraft(p => ({ ...p, address1: e.target.value }))} style={inputStyle} /></div>
                          <div><label style={labelStyle}>City</label><input value={draft.city || ""} onChange={e => setDraft(p => ({ ...p, city: e.target.value }))} style={inputStyle} /></div>
                          <div><label style={labelStyle}>State</label><input value={draft.state || ""} onChange={e => setDraft(p => ({ ...p, state: e.target.value }))} style={inputStyle} /></div>
                          <div><label style={labelStyle}>Postal</label><input value={draft.postal || ""} onChange={e => setDraft(p => ({ ...p, postal: e.target.value }))} style={inputStyle} /></div>
                        </div>
                      </div>

                      {/* Partner Functions */}
                      <div style={{ background: "#f0f9ff", borderRadius: 8, padding: 16 }}>
                        <div style={{ fontWeight: 700, marginBottom: 12 }}>🏷️ Partner Functions</div>
                        <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                          {PARTNER_FUNCTIONS.map(fn => {
                            const active = (draft.partnerFunctions || []).includes(fn.code);
                            return (
                              <button key={fn.code} onClick={() => toggleFunction(fn.code)}
                                style={{
                                  padding: "6px 12px", borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: "pointer",
                                  border: active ? "2px solid #2563eb" : "1px solid #d1d5db",
                                  background: active ? "#dbeafe" : "white", color: active ? "#1d4ed8" : "#374151"
                                }}>
                                {fn.icon} {fn.label}
                              </button>
                            );
                          })}
                        </div>
                      </div>

                      {/* Relationships */}
                      <div style={{ background: "#f9fafb", borderRadius: 8, padding: 16 }}>
                        <div style={{ fontWeight: 700, marginBottom: 12 }}>🔗 Relationships</div>
                        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12 }}>
                          <div>
                            <label style={labelStyle}>Parent Organization</label>
                            <select value={draft.parentCompanyId || ""} onChange={e => setDraft(p => ({ ...p, parentCompanyId: e.target.value }))} style={inputStyle}>
                              <option value="">None</option>
                              {shipperOptions.filter(s => s.id !== editingId).map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                            </select>
                          </div>
                          <div>
                            <label style={labelStyle}>Billing Party</label>
                            <select value={draft.billingCompanyId || ""} onChange={e => setDraft(p => ({ ...p, billingCompanyId: e.target.value }))} style={inputStyle}>
                              <option value="">Same as company</option>
                              {shipperOptions.filter(s => s.id !== editingId).map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                            </select>
                          </div>
                          <div><label style={labelStyle}>Billing Email</label><input value={draft.billingEmail || ""} onChange={e => setDraft(p => ({ ...p, billingEmail: e.target.value }))} style={inputStyle} /></div>
                        </div>
                      </div>

                      {/* Contacts */}
                      <div style={{ background: "#f9fafb", borderRadius: 8, padding: 16 }}>
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
                          <div style={{ fontWeight: 700 }}>👥 Contacts</div>
                          <button onClick={() => setShowAddContact(!showAddContact)}
                            style={{ padding: "4px 12px", fontSize: 13, fontWeight: 600, borderRadius: 6, border: "1px solid #d1d5db", background: "white", cursor: "pointer" }}>
                            {showAddContact ? "Cancel" : "+ Add Contact"}
                          </button>
                        </div>

                        {showAddContact && (
                          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr auto", gap: 8, marginBottom: 12, padding: 12, background: "white", borderRadius: 8, border: "1px solid #e5e7eb" }}>
                            <input value={contactDraft.firstName} onChange={e => setContactDraft(p => ({ ...p, firstName: e.target.value }))} placeholder="First Name" style={inputStyle} />
                            <input value={contactDraft.lastName} onChange={e => setContactDraft(p => ({ ...p, lastName: e.target.value }))} placeholder="Last Name" style={inputStyle} />
                            <input value={contactDraft.email} onChange={e => setContactDraft(p => ({ ...p, email: e.target.value }))} placeholder="Email" style={inputStyle} />
                            <input value={contactDraft.phone} onChange={e => setContactDraft(p => ({ ...p, phone: e.target.value }))} placeholder="Phone" style={inputStyle} />
                            <button onClick={addContact} style={{ padding: "10px 14px", fontWeight: 700, background: "#2563eb", color: "white", border: "none", borderRadius: 6, cursor: "pointer", whiteSpace: "nowrap" }}>Add</button>
                          </div>
                        )}

                        {c.contacts?.length > 0 ? (
                          <div style={{ display: "grid", gap: 6 }}>
                            {c.contacts.map(co => (
                              <div key={co.id} style={{ display: "flex", alignItems: "center", gap: 12, padding: 8, background: "white", borderRadius: 6, border: "1px solid #e5e7eb", fontSize: 13 }}>
                                <span style={{ fontWeight: 600 }}>{co.first_name} {co.last_name}</span>
                                {co.title && <span style={{ color: "#6b7280" }}>{co.title}</span>}
                                {co.email && <span style={{ color: "#2563eb" }}>{co.email}</span>}
                                {co.phone && <span style={{ color: "#6b7280" }}>{co.phone}</span>}
                                {co.is_primary && <Badge label="Primary" color="#059669" bg="#d1fae5" />}
                                <button onClick={() => deleteContact(co.id)}
                                  style={{ marginLeft: "auto", padding: "2px 8px", fontSize: 12, color: "#dc2626", background: "none", border: "1px solid #fca5a5", borderRadius: 4, cursor: "pointer" }}>✕</button>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <div style={{ fontSize: 13, color: "#9ca3af" }}>No contacts added yet.</div>
                        )}
                      </div>

                      {/* Save */}
                      <div style={{ display: "flex", gap: 12, justifyContent: "flex-end" }}>
                        <button onClick={() => { setEditingId(null); setDraft({}); }}
                          style={{ padding: "10px 20px", fontWeight: 700, borderRadius: 6, border: "1px solid #d1d5db", background: "#f3f4f6", cursor: "pointer" }}>
                          Cancel
                        </button>
                        <button onClick={save} disabled={saving}
                          style={{ padding: "10px 24px", fontWeight: 700, borderRadius: 6, border: "none", background: "#2563eb", color: "white", cursor: "pointer" }}>
                          {saving ? "Saving..." : "💾 Save Changes"}
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
